import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

const VARIANT_CLASS = {
  primary: 'px-4 py-2 rounded-lg bg-[#3F707D] hover:bg-[#2d5f6e] text-white',
  secondary: 'px-[15px] py-[7px] rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
  outline: 'px-[15px] py-[7px] rounded-lg border border-[#3F707D] text-[#3F707D] hover:bg-[#3F707D]/5',
  tertiary: 'px-4 py-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600',
  danger: 'px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white',
  // Plain text trigger styled like an inline link — no padding, background, or
  // border-radius chrome, since it sits inline with body text rather than in
  // a toolbar/footer (e.g. "Change" next to an email address, "Resend code").
  link: 'text-[#003DE6] hover:underline font-medium',
} as const;

export type ButtonVariant = keyof typeof VARIANT_CLASS;

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  loading?: boolean;
  loadingLabel?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
}

// Shared primary/secondary/danger button used in every drawer/modal footer
// and page toolbar. `loading` swaps the label and disables the button so
// callers don't have to repeat the `disabled || saving` + label-swap boilerplate.
// `leftIcon`/`rightIcon` render a small icon (SVG element) on either side of
// the label; they're hidden while `loading` since loadingLabel replaces the
// whole label+icons row.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  loading = false,
  loadingLabel,
  leftIcon,
  rightIcon,
  disabled = false,
  className = '',
  children,
  ...rest
}, ref) => (
  <button
    ref={ref}
    disabled={disabled || loading}
    className={`inline-flex items-center justify-center gap-2 text-sm font-medium cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none ${VARIANT_CLASS[variant]} ${className}`}
    {...rest}
  >
    {loading ? (
      loadingLabel ?? children
    ) : (
      <>
        {leftIcon && <span className="shrink-0 w-4 h-4 inline-flex items-center justify-center">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="shrink-0 w-4 h-4 inline-flex items-center justify-center">{rightIcon}</span>}
      </>
    )}
  </button>
));
Button.displayName = 'Button';
